import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Keeps scroll behaviour predictable across route changes:
 * - PUSH / REPLACE navigations always start at the top of the new page.
 * - POP (back/forward) restores the previous scroll offset.
 * - Hash links (`/page#section`) scroll to the target element instead.
 */
const positions = new Map<string, number>();

const ScrollRestoration = () => {
  const { pathname, search, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const lastKey = useRef(key);

  // Persist the scroll offset of the page we are leaving.
  useEffect(() => {
    const save = () => positions.set(lastKey.current, window.scrollY);
    window.addEventListener('scroll', save, { passive: true });
    return () => {
      save();
      window.removeEventListener('scroll', save);
    };
  }, [pathname, search]);

  useEffect(() => {
    lastKey.current = key;

    // Disable the browser's own (async, often wrong) restoration.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      // Wait a frame so lazily-rendered content exists.
      const raf = requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo({ top: 0, left: 0 });
      });
      return () => cancelAnimationFrame(raf);
    }

    if (navigationType === 'POP') {
      const y = positions.get(key) ?? 0;
      const raf = requestAnimationFrame(() => window.scrollTo({ top: y, left: 0 }));
      return () => cancelAnimationFrame(raf);
    }

    window.scrollTo({ top: 0, left: 0 });
  }, [pathname, search, hash, key, navigationType]);

  return null;
};

export default ScrollRestoration;
